'use client';

// GoogleDocPreviewCard - Display extracted Google Sheets/Docs content with preview and status

import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import { useTranslations } from 'next-intl';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import TableChartIcon from '@mui/icons-material/TableChart';
import DescriptionIcon from '@mui/icons-material/Description';

export type GoogleDocStatus = 'pending' | 'extracting' | 'ready' | 'error';

export interface GoogleDocContent {
  /** Unique identifier for the content (local tracking ID) */
  id: string;
  /** Database content ID (for deletion sync) */
  contentId?: string;
  /** Document title */
  title: string;
  /** The original Google document URL */
  url: string;
  /** Preview of extracted content (first 500 chars) */
  excerpt?: string;
  /** Character count of extracted content */
  characterCount?: number;
  /** Type of Google document */
  sourceType: 'google_sheets' | 'google_docs';
  /** Current processing status */
  status: GoogleDocStatus;
  /** Error message if extraction failed */
  errorMessage?: string;
  /** Error code for i18n lookup */
  errorCode?: string;
}

export interface GoogleDocPreviewCardProps {
  /** The extracted Google document content */
  content: GoogleDocContent;
  /** Callback to remove the content */
  onRemove: (contentId: string) => void;
  /** Whether the card is disabled */
  disabled?: boolean;
}

/**
 * Truncates a URL for display
 */
function truncateUrl(url: string, maxLength: number = 60): string {
  if (url.length <= maxLength) return url;
  const start = url.substring(0, maxLength - 10);
  const end = url.substring(url.length - 7);
  return `${start}...${end}`;
}

/**
 * GoogleDocPreviewCard - Displays extracted Google Sheets/Docs content
 *
 * Features:
 * - Document title and URL display
 * - Google Sheets/Docs icon and badge
 * - Character count of extracted content
 * - Expandable content preview
 * - Remove button with confirmation
 * - Error state display
 * - RTL support via CSS logical properties
 *
 * @example
 * ```tsx
 * <GoogleDocPreviewCard
 *   content={googleDocContent}
 *   onRemove={(id) => removeContent(id)}
 * />
 * ```
 */
export function GoogleDocPreviewCard({
  content,
  onRemove,
  disabled = false,
}: GoogleDocPreviewCardProps) {
  const t = useTranslations('creator.googleExtraction');
  const [isExpanded, setIsExpanded] = useState(false);

  const isLoading = content.status === 'pending' || content.status === 'extracting';
  const hasError = content.status === 'error';
  const isSheets = content.sourceType === 'google_sheets';

  // Determine border color based on status
  const getBorderColor = () => {
    if (hasError) return 'error.light';
    if (content.status === 'ready') return 'success.light';
    return 'divider';
  };

  // Determine background color based on status
  const getBackgroundColor = () => {
    if (hasError) return 'error.lighter';
    return 'background.paper';
  };

  // Get status icon
  const getStatusIcon = () => {
    if (content.status === 'ready') {
      return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />;
    }
    if (hasError) {
      return <ErrorIcon data-testid="ErrorIcon" sx={{ color: 'error.main', fontSize: 20 }} />;
    }
    return null;
  };

  // Get Google document icon
  const getDocIcon = () => {
    if (isSheets) {
      return (
        <TableChartIcon
          sx={{
            color: hasError ? 'error.main' : '#0F9D58', // Google Sheets green
          }}
        />
      );
    }
    return (
      <DescriptionIcon
        sx={{
          color: hasError ? 'error.main' : '#4285F4', // Google Docs blue
        }}
      />
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: getBorderColor(),
        borderRadius: 1,
        backgroundColor: getBackgroundColor(),
      }}
    >
      {/* Header row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
        }}
      >
        {/* Document icon */}
        <Box sx={{ flexShrink: 0, pt: 0.25 }}>{getDocIcon()}</Box>

        {/* Document info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography
              variant="body2"
              fontWeight="medium"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
              }}
            >
              {content.title}
            </Typography>
            <Chip
              label={isSheets ? t('preview.sheetsTitle') : t('preview.docsTitle')}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                backgroundColor: isSheets ? '#E8F5E9' : '#E3F2FD',
                color: isSheets ? '#0F9D58' : '#4285F4',
                flexShrink: 0,
              }}
            />
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            component="a"
            href={content.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {truncateUrl(content.url)}
          </Typography>
        </Box>

        {/* Status indicator */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {getStatusIcon()}
        </Box>

        {/* Remove button */}
        <IconButton
          size="small"
          onClick={() => onRemove(content.id)}
          disabled={disabled || isLoading}
          aria-label={t('preview.removeContent')}
          sx={{
            flexShrink: 0,
            '&:hover': {
              color: 'error.main',
            },
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Progress bar for loading state */}
      {isLoading && (
        <Box sx={{ mt: 1.5 }}>
          <LinearProgress variant="indeterminate" sx={{ borderRadius: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {isSheets ? t('input.fetchingSheets') : t('input.fetchingDocs')}
          </Typography>
        </Box>
      )}

      {/* Error message */}
      {hasError && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="error">
            {t(`error.${content.errorCode || 'extractionFailed'}`)}
          </Typography>
          {content.errorCode === 'PRIVATE_DOCUMENT' && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {t('error.privateDocumentHint')}
            </Typography>
          )}
        </Box>
      )}

      {/* Content preview */}
      {content.status === 'ready' && content.excerpt && (
        <Box sx={{ mt: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 0.5,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {t('preview.charactersExtracted', { count: content.characterCount || 0 })}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setIsExpanded(!isExpanded)}
              aria-label={t('preview.editContent')}
              sx={{ ml: 1 }}
            >
              {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Box>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              backgroundColor: 'action.hover',
              p: 1,
              borderRadius: 1,
              fontSize: '0.75rem',
              lineHeight: 1.4,
              maxHeight: isExpanded ? 'none' : 80,
              overflow: 'hidden',
              display: isExpanded ? 'block' : '-webkit-box',
              WebkitLineClamp: isExpanded ? 'unset' : 3,
              WebkitBoxOrient: 'vertical',
              fontFamily: isSheets ? 'monospace' : 'inherit',
            }}
          >
            {content.excerpt}
          </Typography>

          <Collapse in={isExpanded}>
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Typography
                variant="caption"
                color="primary"
                sx={{
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
                onClick={() => setIsExpanded(false)}
              >
                {t('preview.editContent')}
              </Typography>
            </Box>
          </Collapse>
        </Box>
      )}
    </Paper>
  );
}

/**
 * GoogleDocPreviewList - Displays a list of extracted Google documents
 */
export interface GoogleDocPreviewListProps {
  /** List of extracted Google documents */
  contents: GoogleDocContent[];
  /** Callback to remove content */
  onRemove: (contentId: string) => void;
  /** Whether the list is disabled */
  disabled?: boolean;
}

export function GoogleDocPreviewList({
  contents,
  onRemove,
  disabled = false,
}: GoogleDocPreviewListProps) {
  if (contents.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        mt: 2,
      }}
    >
      {contents.map((content) => (
        <GoogleDocPreviewCard
          key={content.id}
          content={content}
          onRemove={onRemove}
          disabled={disabled}
        />
      ))}
    </Box>
  );
}
